import telenorid from "~/telenorid/telenorid";
import { ShoppingCartItemEnum } from "~/components/ecommerce/types/ShoppingCartItemEnum";
import { ProductCategory } from "~/components/ecommerce/types/ProductCategory";
import { useUserStore } from "~/stores/platform/user/user.ts";

import validateDeliveryAddress from '../utils/eCommerce/validateDeliveryAddress.js';

export const state = () => {
	return {
		guest: false,
		manualCurrentTerminal: null,
		graph: null,
		loading: false,
		msisdnDetails: [],
		validSubscription: false,
		paymentMethod: {},
		reservedNumbers: [],
		addonsUpdated: false,
		subscriptions: [],
		needsSubscription: false,
		isTelenorButikkenSelected: false,
		validatingAddressId: false,
	};
};
export const mutations = {
	setCurrentTerminal(state, currentTerminal) {
		state.manualCurrentTerminal = currentTerminal;
	},
	setGraph(state, graph) {
		state.graph = graph;
	},
	setSubscriptions(state, subscriptions) {
		state.subscriptions = subscriptions;
	},
	setLoading(state, loading) {
		state.loading = loading;
	},
	setReservedNumbers(state, numbers) {
		state.reservedNumbers = numbers;
	},
	setMsisdnDetails(state, msisdn) {
		state.msisdnDetails.push(msisdn);
	},
	removeMsisdnDetails(state, accountId) {
		state.msisdnDetails = state.msisdnDetails.filter((x) => x.oldSubscriptionDetails.accountId !== accountId);
	},
	resetMsisdnDetails(state) {
		state.msisdnDetails = [];
	},
	setValidSubscription(state, valid) {
		state.validSubscription = valid;
	},
	setAddonsUpdated(state, status) {
		state.addonsUpdated = status;
	},
	setNeedSubscription(state, status = false) {
		state.needsSubscription = status;
	},
	setIsTelenorButikkenSelectedAsDelivery(state, value) {
		state.isTelenorButikkenSelectedAsDelivery = value;
	},
};
export const getters = {
	tradeInItem(state, getters, rootState) {
		return rootState["shoppingCart"]?.items?.find((item) => item.type === ShoppingCartItemEnum.TRADE_IN);
	},

	terminals(state, getters, rootState) {
		return rootState["shoppingCart"].items.filter(
			(item) => item.type === "hardware" || item.type === ShoppingCartItemEnum.TRADE_IN,
		);
	},
	ordersProcessed(state, getters) {
		return getters.terminals?.length && !getters.terminals.some((terminal) => !terminal?.metadata?.processed);
	},
	hasPaymentReference(state, getters) {
		return getters.terminals.some(
			(terminal) => terminal?.metadata?.payment?.method?.externalPaymentReference && !terminal?.metadata?.processed,
		);
	},
	isProcessed(state, getters) {
		return getters.terminals.some((terminal) => terminal?.metadata?.processed);
	},
	onlyAccessoryProducts(state, getters) {
		const itemsNotCountingAsAccessory = [
			ProductCategory.PHONE,
			ProductCategory.GOOD_AS_NEW,
			ProductCategory.WATCH,
			ProductCategory.TABLET,
		];
		return (
			getters.terminals?.length &&
			!getters.terminals.some((terminal) => itemsNotCountingAsAccessory.includes(terminal?.metadata?.category))
		);
	},
	hasMobileOrders(state, getters) {
		return getters.terminals.length && !getters.onlyAccessoryProducts;
	},
	hasUnconfiguredTerminals(state, getters) {
		return (
			getters.terminals.some((terminal) => !terminal?.metadata?.terminalConfigured) &&
			!getters.hasPaymentReference &&
			!getters.isProcessed
		);
	},
	buyWithoutSubscription(state, getters) {
		return getters.terminals.some((terminal) => terminal?.metadata?.buyWithoutSubscription);
	},
	authSelected() {
		const userStore = useUserStore();
		return userStore.customer;
	},

	orderingNewSwap(state, getters) {
		return !!getters.currentTerminal?.metadata?.swap;
	},
	isNotWifiOrGPSProduct(state, getters) {
		const modelIsWifi = getters.currentTerminal?.metadata?.model?.toLowerCase() === "wi-fi"; // checking exact model name because name can include wi-fi but also have 4g/5g support
		const gpsAndWifiNameCombinations = ["wi-fi", "wifi", "wi fi", "gps"];
		const isWifiOrGps = gpsAndWifiNameCombinations.some((combination) =>
			getters.currentTerminal?.metadata?.name.toLowerCase().includes(combination),
		);
		return !(modelIsWifi || isWifiOrGps);
	},
	notOrderingNewSwap(state, getters) {
		return !getters.orderingNewSwap;
	},
	allTerminalsConfigured(state, getters) {
		let hasPaymentRefInQuery;
		// temp workaround to fix server side issue
		try {
			hasPaymentRefInQuery = window && window.$nuxt?.$route?.query?.ref;
		} catch (e) {
			hasPaymentRefInQuery = false;
			console.error(e);
		}

		if (getters?.hasPaymentReference || getters?.ordersProcessed || !getters.terminals?.length || hasPaymentRefInQuery)
			return false;
		if (!getters.hasUnconfiguredTerminals) return true;
	},
	currentTerminal(state, getters) {
		// Composable exist
		if (state.manualCurrentTerminal) return state.manualCurrentTerminal;
		return getters.terminals.find(
			(terminal) => !terminal?.metadata?.terminalConfigured && terminal?.type === "hardware",
		);
	},
	isValidDeliveryAddress(state, getters) {
		return !!(getters.currentTerminal?.metadata?.deliveryDetails?.addressType === "STREET_ADDRESS");
	},
	isValidAddressId(state, getters) {
		if (!getters.currentTerminal?.metadata?.deliveryDetails) {
			return undefined;
		}

		return validateDeliveryAddress(getters.currentTerminal?.metadata?.deliveryDetails);
	},
	hasValidDeliveryService(state, getters) {
		const deliveryDetails = getters.currentTerminal?.metadata?.deliveryDetails;

		return !!(
			deliveryDetails?.deliveryService === "POSTEN" ||
			(deliveryDetails?.deliveryService === "PORTERBUDDY" && deliveryDetails?.deliveryWindow?.token)
		);
	},
	currentTerminalAndTerminalsNotConfigured(state, getters) {
		return getters.currentTerminal && getters.hasUnconfiguredTerminals;
	},
	loading(state, getters, rootState) {
		return rootState["shoppingCart"].loading;
	},
	currentTerminalHasSubscription(state, getters) {
		return getters.currentTerminal?.subItems?.find((item) => item.type === "subscription");
	},
	currentTerminalInsurance(state, getters) {
		return getters.currentTerminal?.subItems?.find((item) => item.type === "agreement");
	},
	msisdnDetails(state) {
		return state.msisdnDetails;
	},
	validSubscription(state, getters) {
		// Composable exist (useHasValidSubscription)
		return !!getters.currentTerminal?.subItems?.find((item) => item.type === "msisdn");
	},
	validSubscriptionAndNotChangeSwap(state, getters) {
		return getters.validSubscription && !getters.changeSwap;
	},
	validSubscriptionAndNotBuyWithoutSubscription(state, getters) {
		return getters.validSubscription && !getters.buyWithoutSubscription;
	},
	payingWithSwap(state, getters) {
		return !!getters.currentTerminal?.metadata?.swap?.agreementProductId;
	},
	hasNoSubscription(state, getters) {
		// Composable exist
		const isWifiProduct = !getters.isNotWifiOrGPSProduct;
		return !getters.validSubscription || isWifiProduct;
	},
	hasNoServicesToOffer(rootState) {
		return !(rootState.services?.filteredOfferedServices.length || !rootState.services?.hasBeenFetched);
	},

	paymentMethod(state) {
		return state.paymentMethod;
	},
	testWatchAndTabletInsurance() {
		// TO BE Removed once tablet && watch insurance is tested
		return true;
	},

	skipServices(state, getters) {
		// TO BE Removed once tablet && watch insurance is tested
		return getters.currentTerminal?.metadata?.category === ProductCategory.WATCH;
	},
	notSkipServices(state, getters) {
		// TO BE Removed once tablet && watch insurance is tested
		return !getters.skipServices;
	},
	needsIdentification(state, getters) {
		// Composable exist
		return getters.currentTerminal?.metadata?.downPayment && getters.hasNoSubscription;
	},
	notNeedsIdentification(state, getters) {
		// Composable exist (use !needsIdentification)
		return !getters.needsIdentification;
	},
	needSubscription(state, getters) {
		// User needs to have a subscription to order a terminal with down payment
		if (getters.currentTerminal?.metadata?.downPayment) return true;
		return state.needsSubscription;
	},
	swapHasMsisdn(state, getters) {
		return !!getters?.tradeInItem?.metaData?.phoneData?.msisdn;
	},
	isTradeIn(state, getters) {
		return !getters.tradeInItem?.metaData?.hasSWAP;
	},
	isTelenorButikkenSelectedAsDelivery(state) {
		return state.isTelenorButikkenSelectedAsDelivery;
	},
};
export const actions = {
	async setValidSubscription({ commit }, value) {
		commit("setValidSubscription", value);
	},
	async setAddonsUpdated({ commit }, value) {
		commit("setAddonsUpdated", value);
	},
	async init({ commit, dispatch }) {
		commit("setLoading", true);

		await telenorid.getOrLoginUser();

		try {
			await dispatch("shoppingCart/init", null, { root: true });

			console.log("Ready");
		} catch (err) {
			console.error(err);
			throw err;
		} finally {
			commit("setLoading", false);
		}
	},
};
